import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/api/client";
import { useAppStore } from "@/store/app-store";
import type { components } from "@/api/types";

type GatewayFleetEntryRead = components["schemas"]["GatewayFleetEntryRead"];
type GatewayFleetSummaryRead = components["schemas"]["GatewayFleetSummaryRead"];

export type GatewayEnrollmentResponse = {
  gateway_device_id: string;
  serial: string;
  credential: string;
};

export function useGatewayFleet() {
  // Scope the cache by the active department: gateway-fleet is dept-scoped (a
  // respai_admin re-targets it via the X-RespAi-Department-ID header), so the key
  // must carry the department or two tenants collide on one cache entry.
  const deptId = useAppStore((s) => s.selectedDepartmentId);
  return useQuery({
    queryKey: ["gateway-fleet", deptId],
    queryFn: () => api.get<GatewayFleetEntryRead[]>("/gateway-fleet"),
  });
}

export function useGatewayFleetSummary() {
  const deptId = useAppStore((s) => s.selectedDepartmentId);
  return useQuery({
    queryKey: ["gateway-fleet-summary", deptId],
    queryFn: () => api.get<GatewayFleetSummaryRead>("/gateway-fleet/summary"),
  });
}

export function useEnrollGateway() {
  // Enroll a gateway into the active department (the X-RespAi-Department-ID header
  // the client adds picks the switcher's department server-side) and hand back its
  // one-time bearer credential. Refresh the fleet + summary on success.
  const qc = useQueryClient();
  const deptId = useAppStore((s) => s.selectedDepartmentId);
  return useMutation({
    mutationFn: (serial: string) =>
      api.post<GatewayEnrollmentResponse>("/pilot/gateways", { serial }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["gateway-fleet", deptId] });
      qc.invalidateQueries({ queryKey: ["gateway-fleet-summary", deptId] });
    },
  });
}

export function useDecommissionGateway() {
  // Retire a gateway from the active department's fleet. Soft-decommission on the
  // server (revoke credential, close any binding, free the serial) — never a hard
  // delete; the row + audit history are retained but it drops out of the fleet view.
  const qc = useQueryClient();
  const deptId = useAppStore((s) => s.selectedDepartmentId);
  return useMutation({
    mutationFn: (serial: string) =>
      api.delete<void>(`/pilot/gateways/${encodeURIComponent(serial)}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["gateway-fleet", deptId] });
      qc.invalidateQueries({ queryKey: ["gateway-fleet-summary", deptId] });
    },
  });
}
